<template>
    <aside
        :class="[
            useSideBarStore.isOpen ? 'bg-nav-bg items-start' : 'bg-nav-bg items-center',
            'relative border-r border-r-nav-border shrink-0 hidden md:flex flex-col overflow-hidden h-screen max-h-screen transition-colors duration-300 ease-in-out py-5',
            'sidebar-legacy',
        ]">
        <div class="w-full" :class="[sidebarPadding, { 'flex justify-center': !useSideBarStore.isOpen }]">
            <BaseTooltip side="right">
                <template #trigger>
                    <BaseButton
                        data-testid="sidebar-button"
                        type="button"
                        theme="tertiary"
                        icon-only
                        :aria-expanded="useSideBarStore.isOpen"
                        :aria-label="sidebarToggleLabel"
                        class="group"
                        @click="handleSidebarToggle">
                        <!-- Default icons (shown when not hovering) -->
                        <i class="bi bi-layout-sidebar text-2xl group-hover:hidden" aria-hidden="true"></i>

                        <!-- Hover icons (shown only when hovering) -->
                        <i v-if="useSideBarStore.isOpen" class="bi bi-arrow-bar-left text-2xl hidden group-hover:block" aria-hidden="true"></i>
                        <i v-else class="bi bi-arrow-bar-right text-2xl hidden group-hover:block" aria-hidden="true"></i>
                    </BaseButton>
                </template>
                <template #content>{{ sidebarToggleLabel }}</template>
            </BaseTooltip>
        </div>
        <div v-if="!userDetails?.is_anonymous" class="mt-4 mb-10 grow flex overflow-y-auto w-full h-full" :class="pane ? 'max-w-87.5' : 'max-w-[227px]'">
            <div v-if="pane" class="overflow-hidden flex flex-col grow" :class="[sidebarPadding]">
                <div class="border-b border-b-nav-pane-divider shrink-0 pb-2">
                    <BaseButton type="button" theme="tertiary" size="sm" :title="t('navigation.sidebar.return')" @click="clearPane">
                        <span class="bi bi-chevron-left mr-1" aria-hidden="true"></span>
                        <span v-text="pane.title"></span>
                    </BaseButton>
                </div>
                <div class="grow overflow-hidden flex flex-col">
                    <div v-if="loading" class="flex p-4 w-full justify-center">
                        <div class="w-8 h-8 text-nav-loading">
                            <LoadingSpinner />
                        </div>
                    </div>
                    <BaseNotification v-else-if="error" theme="error" hide-dismiss class="m-4" :text="t('navigation.sidebar.loadFailed')" />
                    <div v-else-if="paneComponent" class="flex flex-col grow overflow-y-auto">
                        <component :is="paneComponent.component" v-bind="paneComponent.props" />
                    </div>
                </div>
            </div>
            <!-- Use v-show instead of v-if to keep MainMenu's internal state -->
            <LegacyMainMenu
                v-show="!pane"
                :show-labels="useSideBarStore.isOpen"
                :show-section-titles="useSideBarStore.isOpen"
                :enabled-panes="mainMenuPanes"
                :panes-with-notifications="panesWithNotifications"
                :pane-notification-counts="paneNotificationCounts"
                :class="[sidebarPadding, { 'items-start': useSideBarStore.isOpen, 'items-center': !useSideBarStore.isOpen }]"
                :loading-work-panes="!panesReady"
                @open-pane="openPane" />
        </div>

        <div v-if="useSideBarStore.isOpen" class="mb-6 w-full" :class="[sidebarPadding]">
            <OrganizationLogo image-class="h-5 w-auto object-contain max-w-[150px] mb-4" />
            <p v-if="useSideBarStore.isOpen && userDetails?.can_receive_emails" class="body-small text-nav-text-muted break-all max-w-[227px] fs-mask">
                {{ userDetails?.email }}
            </p>
            <HorizontalDivider as="hr" spacing="none" class="border-t-2 border-nav-divider mt-6" />
        </div>

        <div class="hidden md:flex flex-col gap-2">
            <div v-if="userDetails?.is_customer_demo_accounts_enabled" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseLink data-testid="nav-demo-accounts" href="/demo-accounts/" :aria-label="t('navigation.tools.demoAccounts')" :class="footerItemClass">
                            <i class="bi bi-person-badge nav-icon-legacy leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 text-left">{{ t("navigation.tools.demoAccounts") }}</span>
                        </BaseLink>
                    </template>
                    <template #content>{{ t("navigation.tools.demoAccounts") }}</template>
                </BaseTooltip>
            </div>

            <div v-if="userDetails?.can_see_client_analytics" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseLink data-testid="nav-analytics" href="/client-analytics/" :aria-label="t('navigation.tools.analytics')" :class="footerItemClass">
                            <i class="bi bi-graph-up nav-icon-legacy leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 text-left">{{ t("navigation.tools.analytics") }}</span>
                        </BaseLink>
                    </template>
                    <template #content>{{ t("navigation.tools.analytics") }}</template>
                </BaseTooltip>
            </div>

            <div v-if="orgDetails?.team_tools_link_enabled" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <a
                            data-testid="nav-team-tools"
                            :href="alignHost"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="t('navigation.tools.teamTools')"
                            :class="footerItemClass"
                            @click="logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: 'team-tools', title: 'Valence Team Tools' })">
                            <i class="bi bi-box-arrow-up-right nav-icon-legacy leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 text-left">{{ t("navigation.tools.teamTools") }}</span>
                        </a>
                    </template>
                    <template #content>{{ t("navigation.tools.teamTools") }}</template>
                </BaseTooltip>
            </div>

            <div v-if="userDetails?.nadia_tutorial_enabled" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseLink
                            data-testid="nav-nadia-tutorial"
                            href="/nadia-tutorial/"
                            :aria-label="t('navigation.tools.nadiaTutorial')"
                            class="body-small text-nav-item-text hover:text-nav-item-text-hover transition-colors flex flex-row items-center leading-none!"
                            @click="logNadiaTutorialClick">
                            <i class="bi bi-question-circle nav-icon leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 flex flex-row items-center text-left">{{ t("navigation.tools.nadiaTutorial") }}</span>
                        </BaseLink>
                    </template>
                    <template #content>{{ t("navigation.tools.nadiaTutorial") }}</template>
                </BaseTooltip>
            </div>

            <div v-if="!userDetails?.is_anonymous" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseButton
                            data-testid="nav-privacy"
                            type="button"
                            theme="tertiary"
                            size="sm"
                            :icon-only="!useSideBarStore.isOpen"
                            :aria-label="privacyLabel"
                            @click="openPrivacyDialog">
                            <i class="bi bi-shield-lock nav-icon-legacy leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 text-left">{{ privacyLabel }}</span>
                        </BaseButton>
                    </template>
                    <template #content>{{ privacyLabel }}</template>
                </BaseTooltip>
                <PrivacyNoticeDialog v-model:open="privacyDialogOpen" />
            </div>

            <div :class="[sidebarPadding]" class="w-full">
                <LegacySettingsMenu :show-label="useSideBarStore.isOpen" />
            </div>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { OrganizationDetails, UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { useNadiaTutorialAnalytics } from "~vue/composables/useNadiaTutorialAnalytics";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { useSideBarStore } from "~vue/stores/sideBarStore";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, inject, onMounted, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import OrganizationLogo from "../../OrganizationLogo.vue";
import { ALL_PANES, MainMenuPane, type PaneNotificationCounts, privacyRowLabel, validateDisabledPanes } from "../menuPanes";
import LegacyMainMenu from "./LegacyMainMenu.vue";
import LegacySettingsMenu from "./LegacySettingsMenu.vue";

const { t } = useI18n();

interface MenuPane {
    id: string;
    title: string;
    component?: Component;
}

interface PaneComponentSlot {
    component: Component;
    props: Record<string, unknown>;
}

interface Props {
    panesWithNotifications?: MainMenuPane[];
    paneNotificationCounts?: PaneNotificationCounts;
}

withDefaults(defineProps<Props>(), {
    panesWithNotifications: () => [],
    paneNotificationCounts: () => ({}),
});

const disabledPanes = ref<MainMenuPane[]>([
    MainMenuPane.GUIDED_SETUP,
    MainMenuPane.COLLABORATION_PROFILE,
    MainMenuPane.CONNECTIONS,
    MainMenuPane.JOURNEYS,
    MainMenuPane.DEVELOPMENT_JOURNEYS,
    MainMenuPane.MY_WORK,
    MainMenuPane.PERFORMANCE_MANAGEMENT,
    MainMenuPane.COMPETENCY_ASSESSMENT,
    MainMenuPane.SCHNEIDER_ASSESSMENT,
    MainMenuPane.REFLECTIONS,
]);
const pane = ref<MenuPane | null>(null);
const paneComponent = ref<PaneComponentSlot | null>(null);
const error = ref<Record<string, unknown> | null>(null);

const navigationUrl = ref("/navigation/");
const {
    data: navigationData,
    error: navigationError,
    isFetching: loading,
    execute: fetchNavigation,
} = useFetch(navigationUrl, { immediate: false }).get().json<Record<string, unknown>>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const orgDetails = inject<Ref<OrganizationDetails | null>>("organization_details");
const alignHost = inject<Ref<string>>("align_host");
const isDesktop = useMediaQuery("(min-width: 768px)");
const { logLinkViewed, logTutorialEvent } = useNadiaTutorialAnalytics();

// Apply org preference before first paint to avoid open→closed flicker
if (orgDetails?.value?.collapse_navbar_by_default) {
    useSideBarStore.isOpen = false;
} else {
    useSideBarStore.isOpen = true;
}

const panesReady = ref(!useSideBarStore.isOpen);

const mainMenuPanes = computed(() => ALL_PANES.filter((p) => !disabledPanes.value.includes(p)));

const sidebarPadding = "px-5";

const footerItemClass = computed(() => ["menu-item nav-item-legacy flex flex-row items-center -outline-offset-1", useSideBarStore.isOpen ? "nav-item-legacy-bleed" : "w-full"]);

const privacyDialogOpen = ref(false);
const privacyLabel = computed(() => privacyRowLabel(orgDetails?.value?.privacy_link_label));
const sidebarToggleLabel = computed(() => (useSideBarStore.isOpen ? t("navigation.sidebar.collapse") : t("navigation.sidebar.expand")));

function openPrivacyDialog() {
    // English, for Amplitude: event properties must not change with the user's language.
    logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: "privacy", title: orgDetails?.value?.privacy_link_label || "Privacy" });
    privacyDialogOpen.value = true;
}

function clearPane() {
    pane.value = null;
}

function handleSidebarToggle() {
    if (pane.value) {
        clearPane();
    }

    useSideBarStore.toggle();
}

function logNadiaTutorialClick() {
    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_LINK_CLICKED, { entry_source: "sidebar" });
}

async function openPane(p: MenuPane) {
    pane.value = p;
    if (!useSideBarStore.isOpen) {
        useSideBarStore.toggle();
    }

    error.value = null;
    paneComponent.value = null;
    navigationUrl.value = `/navigation/?name=${p.id}`;
    await fetchNavigation();

    if (navigationError.value) {
        error.value = navigationData.value ?? (navigationError.value as Record<string, unknown>);
        return;
    }

    if (p.component && navigationData.value) {
        paneComponent.value = { component: p.component, props: navigationData.value };
    }
}

onMounted(async () => {
    disabledPanes.value = await validateDisabledPanes(disabledPanes.value);
    setTimeout(() => {
        panesReady.value = true;
    }, 300);
});

watch(
    () => isDesktop.value && userDetails?.value?.nadia_tutorial_enabled,
    (visible) => {
        if (visible) {
            logLinkViewed("sidebar", useSideBarStore.isOpen);
        }
    },
    { immediate: true },
);
</script>
